import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { useCallback } from 'react';
import { ImagePickerAsset } from 'expo-image-picker';
import { Platform } from 'react-native';
import imageCompression from 'browser-image-compression';
import { scaleImage } from '../utils';
import { MEDIA_CONFIGS } from '@/constants';

export function useImageCompressor() {
  const needsCompression = useCallback((pickerFile: ImagePickerAsset) => {
    return pickerFile.width > MEDIA_CONFIGS.compression.maxImageWidth || pickerFile.height > MEDIA_CONFIGS.compression.maxImageHeight;
  }, []);

  const compress = useCallback(
    async (pickerFile: ImagePickerAsset): Promise<ImagePickerAsset> => {
      if (!needsCompression(pickerFile)) {
        return pickerFile;
      }

      // 🌐 WEB ROUTE: Use browser-image-compression to prevent Canvas memory crashes
      if (Platform.OS === 'web') {
        try {
          // 1. Fetch the file from the local blob URI
          const response = await fetch(pickerFile.uri);
          const blob = await response.blob();
          const file = new File([blob], pickerFile.fileName || 'image.jpg', { type: blob.type });

          // 2. Safely compress using the web library and web workers
          const compressedFile = await imageCompression(file, {
            maxWidthOrHeight: Math.max(MEDIA_CONFIGS.compression.maxImageWidth, MEDIA_CONFIGS.compression.maxImageHeight),
            useWebWorker: true,
            initialQuality: MEDIA_CONFIGS.compression.quality,
          });
          // 3. Reassign the properties to the asset
          pickerFile.uri = URL.createObjectURL(compressedFile);
          pickerFile.fileSize = compressedFile.size;

          return pickerFile;
        } catch (error) {
          console.warn('Web compression failed, falling back to original image:', error);
          return pickerFile; // Return the original file if compression fails
        }
      }

      // 📱 NATIVE ROUTE: Continue using expo-image-manipulator for iOS/Android
      const scale = scaleImage(pickerFile.width, pickerFile.height, MEDIA_CONFIGS.compression.maxImageWidth, MEDIA_CONFIGS.compression.maxImageHeight);
      return manipulateAsync(pickerFile.uri, [{ resize: { width: scale.width, height: scale.height } }], {
        compress: MEDIA_CONFIGS.compression.quality,
        format: MEDIA_CONFIGS.compression.format,
      }).then(async (res) => {
        pickerFile.uri = res.uri;
        pickerFile.width = res.width;
        pickerFile.height = res.height;
        return pickerFile;
      });
    },
    [needsCompression],
  );

  return {
    compress,
  };
}
